import { forwardRef } from 'react'

import { type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { textVariants } from '../text/text'

export type TextLinkProps = VariantProps<typeof textVariants> &
  React.ComponentPropsWithoutRef<'a'> & {
    truncate?: boolean
  }

/**
 * TextLink's typography, colour and underline, for a control that has to stay
 * a `<button>`, such as a trigger that opens a drawer.
 */
export const textLinkClassName = ({
  variant,
  weight,
  truncate
}: Pick<TextLinkProps, 'variant' | 'weight' | 'truncate'> = {}): string =>
  cn(
    // Ømni rule: links are semibold whatever the tier — except on the
    // one tier whose emphasis weight is bold (caption-sm). An explicit
    // `weight` prop still wins.
    textVariants({
      variant,
      weight: weight ?? (variant === 'caption-sm' ? 'bold' : 'semibold')
    }),
    // !decoration-from-font: the apps' RootLayout applies
    // `& a { text-decoration: underline }` via emotion; the shorthand
    // outspecifies bare utilities and would reset the thickness to auto.
    'text-link-fresh underline !decoration-from-font [text-decoration-skip-ink:none] [text-underline-position:from-font]',
    truncate && 'truncate'
  )

export const TextLink = forwardRef<HTMLAnchorElement, TextLinkProps>(
  ({ variant, weight, truncate, className, children, ...props }, ref) => (
    <a
      ref={ref}
      data-slot='text-link'
      className={cn(
        textLinkClassName({ variant, weight, truncate }),
        className
      )}
      {...props}
    >
      {children}
    </a>
  )
)
TextLink.displayName = 'TextLink'
